/**
 * CoinDisplay Component
 * Shows user's daily coin balance and subscription status
 */

import React, { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Coins, Zap, Crown, AlertCircle, ShieldCheck } from 'lucide-react';

const CoinDisplay = ({ compact = false, sidebar = false }) => {
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!auth.currentUser) {
            setLoading(false);
            return;
        }

        // ★ COST FIX: Use getDoc (one-time read) instead of onSnapshot (realtime listener).
        // CoinDisplay shows daily balance in sidebar — coin data only changes on transcription,
        // so a one-time read is sufficient. Saves Firestore reads on every user doc change.
        const fetchUserData = async () => {
            try {
                const userRef = doc(db, 'users', auth.currentUser.uid);
                const snap = await getDoc(userRef);
                if (snap.exists()) {
                    setUserData(snap.data());
                } else {
                    setUserData({
                        tier: 'free',
                        dailyUsed: 0,
                        dailyLimit: 1
                    });
                }
            } catch (e) {
                console.warn('[CoinDisplay] Failed to fetch user data:', e);
                setUserData({ tier: 'free', dailyUsed: 0, dailyLimit: 1 });
            } finally {
                setLoading(false);
            }
        };
        fetchUserData();
    }, []);

    if (loading) {
        return (
            <div className="coin-display-skeleton animate-pulse">
                <div className={`bg-slate-200 dark:bg-slate-700 rounded-lg ${sidebar ? 'w-8 h-8 mx-auto' : 'h-8 w-24'}`}></div>
            </div>
        );
    }

    if (!userData) {
        return null;
    }

    const { tier, dailyTranscribeUsed, dailyRewriteUsed, dailyUsed: legacyUsed, pipelineCoins } = userData;

    const isAdmin = tier === 'admin';
    const isPaid = tier === 'paid' || isAdmin;
    const isTrial = tier === 'trial';

    // Transcribe Coins Logic
    const usedCoins = dailyTranscribeUsed !== undefined ? dailyTranscribeUsed : (legacyUsed || 0);
    // Admin: Unlimited, Paid: 10, Free: 1
    const coinLimit = isAdmin ? Infinity : (tier === 'paid' ? 10 : 1);
    const remainingCoins = isAdmin ? 999 : Math.max(0, coinLimit - usedCoins);

    // Maps to existing JSX variables
    const dailyLimit = isAdmin ? '∞' : coinLimit;
    const remaining = isAdmin ? '∞' : remainingCoins;

    // Rewrite Quota Logic
    const usedRewrites = dailyRewriteUsed || 0;
    // Admin/Paid: Unlimited (9999), Free: 10
    const rewriteLimit = (tier === 'paid' || isAdmin) ? 9999 : 10;
    const remainingRewrites = rewriteLimit - usedRewrites;
    const isUnlimited = rewriteLimit > 1000;

    const isLow = !isAdmin && remainingCoins === 0;
    const isEmpty = !isAdmin && remainingCoins === 0;

    // Trial user sidebar — show pipeline coins only
    if (sidebar && isTrial) {
        const trialEmpty = (pipelineCoins ?? 0) === 0;
        return (
            <div className="flex flex-col items-center gap-1 cursor-help" title={`Pipeline coins: ${pipelineCoins ?? 0}/5`}>
                <div className={`p-1.5 rounded-lg transition-colors ${trialEmpty ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' : 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                    <Zap size={18} />
                </div>
                <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">{pipelineCoins ?? 0}</span>
            </div>
        );
    }

    if (sidebar) {
        return (
            <div
                className="flex flex-col items-center gap-1 cursor-help group relative"
                title={`Coins: ${remaining}/${dailyLimit}\nRewrites: ${isUnlimited ? 'Unlimited' : remainingRewrites}`}
            >
                <div className={`p-1.5 rounded-lg transition-colors ${isEmpty
                    ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400'
                    : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                    }`}>
                    <Coins size={18} />
                </div>
                <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">
                    {remaining}
                </span>
            </div>
        );
    }

    if (compact) {
        return (
            <div
                className={`coin-compact flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-bold transition-colors ${isEmpty
                    ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400'
                    : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                    }`}
                title={`Coins: ${remaining}/${dailyLimit}\nRewrites: ${isUnlimited ? 'Unlimited' : remainingRewrites}`}
            >
                <Coins size={14} />
                <span>{remaining}/{dailyLimit}</span>
            </div>
        );
    }

    // Trial user full card
    if (isTrial) {
        const coins = pipelineCoins ?? 0;
        const coinsEmpty = coins === 0;
        return (
            <div className="coin-display p-4 rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/10">
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
                        <Zap size={16} className="text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                        <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Percubaan Percuma</p>
                        <p className="text-[10px] text-slate-500">Pipeline AI terhad</p>
                    </div>
                </div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-slate-600 dark:text-slate-400">Pipeline coins</span>
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-lg ${coinsEmpty ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' : 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                        <Zap size={14} />
                        <span>{coins}</span>
                        <span className="text-xs opacity-70">/ 5</span>
                    </div>
                </div>
                <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden mb-3">
                    <div className={`h-full transition-all duration-500 ${coinsEmpty ? 'bg-rose-500' : 'bg-amber-500'}`} style={{ width: `${(coins / 5) * 100}%` }} />
                </div>
                {coinsEmpty ? (
                    <div className="flex flex-col gap-2 p-3 bg-rose-50 dark:bg-rose-900/20 rounded-lg">
                        <p className="text-xs text-rose-600 dark:text-rose-400 font-bold flex items-center gap-1.5">
                            <AlertCircle size={13} /> Coins percubaan habis!
                        </p>
                        <p className="text-xs text-rose-500 dark:text-rose-400">Subscribe untuk teruskan menggunakan pipeline AI tanpa had.</p>
                        <a href="/pricing" className="mt-1 text-center px-3 py-2 bg-rose-500 hover:bg-rose-600 text-white text-xs font-bold rounded-lg transition-colors">
                            Subscribe Sekarang →
                        </a>
                    </div>
                ) : (
                    <p className="text-[10px] text-slate-400 text-center">1 pipeline AI = 1 coin • {coins} produk lagi boleh diproses</p>
                )}
            </div>
        );
    }

    return (
        <div className="coin-display p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    {isAdmin ? (
                        <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                            <ShieldCheck size={16} className="text-white" />
                        </div>
                    ) : isPaid ? (
                        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                            <Crown size={16} className="text-white" />
                        </div>
                    ) : (
                        <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                            <Zap size={16} className="text-slate-600 dark:text-slate-400" />
                        </div>
                    )}
                    <div>
                        <p className="text-xs font-bold text-slate-800 dark:text-slate-200">
                            {isAdmin ? 'System Admin' : (isPaid ? 'Pakej RM99' : 'Free Trial')}
                        </p>
                        <p className="text-[10px] text-slate-500">
                            {isAdmin ? 'Superuser Access' : (isPaid ? 'Unlimited Access' : 'Terhad')}
                        </p>
                    </div>
                </div>
            </div>

            {/* Coin Counter */}
            <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-slate-600 dark:text-slate-400">Coins Hari Ini</span>
                <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold ${isAdmin
                        ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400'
                        : isEmpty
                            ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400'
                            : isLow
                                ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                                : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                    }`}>
                    <Coins size={16} />
                    <span className="text-lg">{remaining}</span>
                    <span className="text-xs opacity-70">/ {dailyLimit}</span>
                </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden mb-3">
                <div
                    className={`h-full transition-all duration-500 ${isAdmin
                            ? 'bg-indigo-500'
                            : isEmpty
                                ? 'bg-rose-500'
                                : isLow
                                    ? 'bg-amber-500'
                                    : 'bg-emerald-500'
                        }`}
                    style={{ width: isAdmin ? '100%' : `${(remainingCoins / coinLimit) * 100}%` }}
                />
            </div>

            {/* Warning Messages */}
            {isEmpty && (
                <div className="flex items-start gap-2 p-2 bg-rose-50 dark:bg-rose-900/20 rounded-lg text-rose-600 dark:text-rose-400">
                    <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                    <p className="text-xs">
                        Limit harian telah habis. Reset pada 12:00 AM.
                        {!isPaid && <span className="block mt-1 font-bold">Upgrade ke Pakej RM99 untuk 10 coins/hari!</span>}
                    </p>
                </div>
            )}

            {isLow && !isEmpty && (
                <div className="flex items-start gap-2 p-2 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-amber-600 dark:text-amber-400">
                    <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                    <p className="text-xs">
                        Tinggal {remaining} coin lagi hari ini.
                    </p>
                </div>
            )}

            {/* Usage Info */}
            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                <p className="text-[10px] text-slate-400 text-center">
                    1 Transcription = 1 Coin • Rewrite Limit Berasingan
                </p>
            </div>
        </div>
    );
};

export default CoinDisplay;
